import Backbone from 'backbone';
import _ from 'underscore';

import BaseObject from 'app/src/base/BaseObject';

const _Event = {
  VIEW_SHOWN: 'ViewShown',
};

export default class BaseController extends BaseObject {
  static get Event() {
    return _Event;
  }

  constructor() {
    super();

    this._view = null;
    this._globalChannel = Backbone.Wreqr.radio.channel('global');
    this._childViewControllers = {};
  }

  /**
   * Sets the view managed by the controller.  If a view was already set, then the existing
   * view is cleaned up and destroyed before assigning and rendering the new one.
   *
   * @param view {*}  The view to be assigned to the controller and rendered.
   */
  set view(view) {
    if (view && _.isFunction(view.render)) {
      if (this._view) {
        // destroy the old view and detach events
        this.stopListening();
        this._detachAllChildViewControllers();
        this._view.destroy();
      }

      this._view = view;
      this.listenTo(this._view, 'all', this._onEvent);
    } else {
      this._view = view;
    }
  }

  /**
   * Returns the view managed by the controller.
   *
   * @returns {BaseController._view|*}
   */
  get view() {
    return this._view;
  }

  /**
   * Shows the set view
   */
  show() {
    if (this.view && this.view.render) {
      this.view.render();
      this.setInitialViewState();
    }
    this.trigger(_Event.VIEW_SHOWN);
  }

  /**
   * Invoked to cleanup the controller before it's discarded.  Cleans up bindings and
   * contained data.
   */
  destroy() {
    this.stopListening();

    this._detachAllChildViewControllers();

    if (this.view) {
      this.view.destroy();
    }

    this.view = null;
  }

  /**
   * Cleans up the controller along with the view, to avoid memory leaks
   */
  onDestroy() {
    this.destroy();
  }

  /**
   * Creates a div region so that we may attach a ChildViewController
   * (typically in an iterative setting but not limited to).
   *
   * @param region {string} ID to assign to the new region div created
   * @param selector {string} container element to append the region to.
   */
  createRegion(region, selector = null) {
    if (this.view && this.view.getRegion && !this.view.getRegion(region)) {
      let $location = selector ? this.view.$el.find(selector) : this.view.$el;

      if ($location.length) {
        $location.append(`<div id="${region}"></div>`);
        this.view.addRegion(region, `#${region}`);
      }
    }
  }

  /**
   * Removes a div region from the view.  Performs any cleanup necessary (e.g. cleanup
   * attached view controllers).
   *
   * @param region {string}   The name of the region to remove.
   */
  removeRegion(region) {
    if (this.view && this.view.getRegion && this.view.getRegion(region)) {
      // cleanup any child view controllers before removing the region
      this._detachChildViewController(region);

      // removing the region doesn't get rid of the created DOM element so let's
      // clean that up as well
      if (this.view.getRegion(region).$el) {
        this.view.getRegion(region).$el.detach();
      }

      // remove the region from the view
      this.view.removeRegion(region);
    }
  }

  /**
   * Similar to showChildView but attaches a view managed by a controller other than
   * this controller.  This pattern could be used to break up the controller logic
   * for controllers that would otherwise have to manage many different views.  Instead,
   * using this method the control logic may be better distributed.  Any attached controllers
   * (and their views) will be automatically cleaned up if another controller is attached
   * to the same region.
   *
   * @param controller {*} Another BaseController derived controller with an attached view
   * @param region
   */
  attachChildViewController(controller, region) {
    // make sure the current view has a region
    if (this.view && this.view.getRegion && this.view.getRegion(region)) {
      // clean up an existing childView controller before assigning
      // a new one
      this._detachChildViewController(region);

      this._childViewControllers[region] = controller;
      this.listenTo(this._childViewControllers[region], 'all', this._onEvent);

      this.showChildView(region, controller.view);
      controller.show();
    }
  }

  /**
   * Retrieves the child view controller attached to a region.
   *
   * @param region {string}   The region of the child view controller
   * @returns {*|null}        The child view controller or null
   */
  getAttachedChildViewController(region) {
    return this._childViewControllers[region] || null;
  }

  /**
   * This method is specific to Layout Views.  It will show a child view in the region
   * specified.  If a childView is already set to the specified region, then it will be
   * cleaned up before the new childView is shown.
   *
   * @param region {string}   The name of the region
   * @param childView {*}     The childView to be assigned to the region and shown
   */
  showChildView(region, childView) {
    if (this.view && this.view.getRegion && this.view.getRegion(region)) {
      if (this.view.getRegion(region).hasView()) {
        this.stopListening(this.view.getRegion(region).currentView);
      }

      try {
        // It's possible for the Register Controller to attempt to
        // clean up after itself before anything is initialized
        // Wrap it in a try block to catch that error if it happens
        this.view.showChildView(region, childView);
        this.listenTo(childView, 'all', this._propagateChildEvents);
      } catch (e) {
        console.log(e.message);
      }
    }
  }

  /**
   * A derived class should override this method to perform any state updates
   * in the view (eg. populate dynamic data)
   */
  setInitialViewState() {}

  /**
   * This method is specific to controllers which manage a Layout View.  Removes an attached
   * child view controller from the specified region.
   *
   * @param region {string}   The name of the region
   * @private
   */
  _detachChildViewController(region) {
    if (this._childViewControllers[region]) {
      this._childViewControllers[region].destroy();
      this.stopListening(this._childViewControllers[region]);
      delete this._childViewControllers[region];
    }
  }

  /**
   * This method is specific to controllers which manage a Layout View.  Removes any attached
   * child view controllers from every region.
   *
   * @private
   */
  _detachAllChildViewControllers() {
    _.each(_.keys(this._childViewControllers), (region) => {
      this._detachChildViewController(region);
    });
  }

  /**
   * Propagates events to the controller.  The controller may act on the events by providing
   * an appropriately named signature.
   *
   * E.g. If the event name is 'do:something' then the controller would create an event handler
   * with the name 'onDoSomething'.
   *
   * @param args {...}    Arguments triggered with the view event
   * @private
   */
  _onEvent(...args) {
    // eslint-disable-next-line prefer-spread
    this.triggerMethod.apply(this, args);
  }

  /**
   * Propagates events triggered by a childView to the controller.  The events will have
   * the same arguments as the original event but with 'childView:' prepended to the triggered
   * event name.  The controller may listen to the events directly [this.listenTo('do:something', callback)]
   * or by providing a event handler method of the appropriate signature [onDoSomething() { ... }].
   *
   * @param args {...}    Arguments triggered with the child event
   */
  _propagateChildEvents(...args) {
    let regex = /^childView:/i;

    // prevent deeper child events from continually pre-pending childView
    if (!regex.test(args[0])) {
      args[0] = `childView:${args[0]}`;
    } else {
      args[0] = args[0].replace(regex, 'childView:');
    }
    // eslint-disable-next-line prefer-spread
    this.triggerMethod.apply(this, args);
  }
}
